import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarBlank, ListChecks, Trash } from "@phosphor-icons/react";

// props: open, onClose, initial (event obj for edit), presetDate, onSaved
export default function EventDialog({ open, onClose, initial, presetDate, onSaved }) {
  const [kind, setKind] = useState("event");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setKind(initial.kind || "event"); setTitle(initial.title || "");
      setDate(initial.date || ""); setTime(initial.time || ""); setNotes(initial.notes || "");
    } else {
      setKind("event"); setTitle(""); setDate(presetDate || ""); setTime(""); setNotes("");
    }
  }, [open, initial, presetDate]);

  const save = async () => {
    if (!title.trim()) return toast.error("Add a title");
    if (!date) return toast.error("Pick a date");
    setBusy(true);
    try {
      const payload = { title: title.trim(), date, time: time || null, kind, notes };
      const res = initial ? await api.updateEvent(initial.id, payload) : await api.createEvent(payload);
      toast.success(initial ? "Updated" : `${kind === "task" ? "Task" : "Event"} added`);
      onSaved?.(res); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await api.deleteEvent(initial.id); toast.success("Removed"); onSaved?.(); onClose(); }
    catch (e) { toast.error("Could not remove"); }
    finally { setBusy(false); }
  };

  const KindBtn = ({ value, icon: Icon, label }) => (
    <button type="button" onClick={() => setKind(value)} data-testid={`event-kind-${value}`}
      className={`flex-1 flex items-center justify-center gap-2 rounded-lg border p-2.5 text-sm font-semibold transition-colors duration-200 ${kind === value ? "border-[#00B4D8] bg-cyan-50 text-slate-800" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
      <Icon size={16} weight="duotone" /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{initial ? "Edit" : "Add to schedule"}</DialogTitle>
          <DialogDescription>Add a calendar event or a to-do task for a specific day.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <KindBtn value="event" icon={CalendarBlank} label="Event" />
            <KindBtn value="task" icon={ListChecks} label="Task" />
          </div>
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="event-title"
              placeholder={kind === "task" ? "e.g. Order more soap" : "e.g. Equipment maintenance"} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="event-date" className="mt-1.5" />
            </div>
            <div>
              <Label>Time <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="event-time" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" data-testid="event-notes" />
          </div>
        </div>

        <DialogFooter className="flex sm:justify-between items-center gap-2">
          {initial ? (
            <Button variant="ghost" onClick={remove} disabled={busy} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="event-delete">
              <Trash size={16} className="mr-1.5" /> Delete
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#00B4D8] hover:bg-cyan-600" data-testid="event-save">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
